import { ReactNode, useEffect, useState } from 'react';
import clsx from 'clsx';
import { Dropdown } from 'react-bootstrap';
import { useIntl } from 'react-intl';

import {
  OrderModel,
  OrderOriginEnum,
  OrderStateEnum,
} from '../models/OrderModel';
import { DistributorModel, listDistributors } from '../../distributors';
import { SaleableProductModel } from '../models/SaleableProductModel';
import { toast } from 'react-toastify';
import { Pagination } from '@mui/material';
import { BulkActionsEnum, ORDERS_PER_PAGE } from '../services/OrderCRUD';
import authorizedLockedToProposalUsers from '../constants/authorizedLockedToProposalUsers';
import { useSelector } from 'react-redux';
import { authUserSelector } from '../../auth';

export interface BulkActionConfig {
  action: BulkActionsEnum;
  i18nKey: string;
  icon: string;
}

const SUCCESS_STATES = [
  OrderStateEnum.SHIPPED,
  OrderStateEnum.COMPLETED,
  OrderStateEnum.SHIPPING_BY_DISTRIBUTOR,
];
const FAIL_STATES = [
  OrderStateEnum.CANCELLED,
  OrderStateEnum.RETURNED,
  OrderStateEnum.UNDELIVERED,
];
const NON_EDITABLE_STATES = [
  OrderStateEnum.CANCELLED,
  OrderStateEnum.COMPLETED,
  OrderStateEnum.LOCKED,
];

export const ORIGIN_LABEL_IDS: Record<OrderOriginEnum, string> = {
  [OrderOriginEnum.DISTRIBUTOR]: 'b2bOrders.originDistributor',
  [OrderOriginEnum.EDI]: 'b2bOrders.originEdi',
  [OrderOriginEnum.CRM]: 'b2bOrders.originCrm',
};

type BULK_STATE = 'loading' | 'idle';

type PropType = {
  onCreateOrder: () => void;
  onParseEdi: () => void;
  onEditOrder: (order: OrderModel) => void;
  onEditComments: (order: OrderModel) => void;
  onEditLines: (order: OrderModel) => void;
  onGenerateProforma: (order: OrderModel) => void;
  onGenerateDeliveryNote: (order: OrderModel) => void;
  onUpdateState: (order: OrderModel, state: OrderStateEnum) => void;
  bulkActions?: BulkActionConfig[];
  onBulkAction?: (
    action: BulkActionsEnum,
    orderIds: Array<number>
  ) => Promise<unknown>;
  orders: Array<OrderModel>;
  page: number;
  count: number;
  onPageChange: (page: number) => void;
  products: Array<SaleableProductModel>;
  loading: boolean;
  children?: ReactNode;
  displayBulkActions?: boolean;
};

export default function OrderList({
  onCreateOrder,
  onParseEdi,
  onEditOrder,
  onEditLines,
  onEditComments,
  onGenerateProforma,
  onGenerateDeliveryNote,
  onUpdateState,
  bulkActions = [],
  onBulkAction,
  onPageChange,
  orders,
  page,
  count,
  loading,
  children,
  displayBulkActions = false,
}: PropType) {
  const intl = useIntl();
  const [distributors, setDistributors] = useState<Array<DistributorModel>>([]);
  const [selectedOrders, setSelectedOrders] = useState<Array<OrderModel>>([]);
  const [bulkState, setBulkState] = useState<BULK_STATE>('idle');
  const hasSelectedAllOrders = selectedOrders?.length === orders?.length;
  const selectedCount = hasSelectedAllOrders ? count : selectedOrders.length;
  const { email } = useSelector(authUserSelector);

  const STATE_TRANSITIONS = {
    [OrderStateEnum.PROPOSAL]: [
      OrderStateEnum.CANCELLED,
      OrderStateEnum.CONFIRMED,
      OrderStateEnum.SHIPPING_BY_DISTRIBUTOR,
    ],
    [OrderStateEnum.CONFIRMED]: [
      OrderStateEnum.LOCKED,
      OrderStateEnum.CANCELLED,
    ],
    [OrderStateEnum.LOCKED]: [
      OrderStateEnum.PROCESSING,
      OrderStateEnum.CANCELLED,
      OrderStateEnum.COMPLETED,
      OrderStateEnum.PARTIALLY_COMPLETED,
    ],
    [OrderStateEnum.PROCESSING]: [
      OrderStateEnum.COMPLETED,
      OrderStateEnum.PARTIALLY_COMPLETED,
      OrderStateEnum.UNDELIVERED,
    ],
    [OrderStateEnum.PARTIALLY_COMPLETED]: [
      OrderStateEnum.COMPLETED,
      OrderStateEnum.UNDELIVERED,
    ],
    [OrderStateEnum.COMPLETED]: [OrderStateEnum.UNDELIVERED],
  };

  if (authorizedLockedToProposalUsers.includes(email)) {
    STATE_TRANSITIONS[OrderStateEnum.LOCKED].push(OrderStateEnum.PROPOSAL);
  }

  async function copyToClipboard(text) {
    try {
      if (navigator?.clipboard) {
        navigator.clipboard.writeText(text);
      } else {
        var textField = document.createElement('textarea');
        textField.textContent = text;
        document.body.appendChild(textField);
        textField.select();
        document?.execCommand('copy');
        textField.remove();
      }
      toast.success(
        intl.formatMessage({ id: 'shopifyOrders.copiedToClipboard' })
      );
    } catch (error) {
      console.warn({ error });
      toast.error(
        intl.formatMessage({ id: 'shopifyOrders.copyToClipboardError' })
      );
    }
  }

  async function fetchDistributors() {
    try {
      const { data } = await listDistributors();
      setDistributors(data);
    } catch (error) {
      console.warn(error);
    }
  }

  useEffect(() => {
    fetchDistributors();
  }, []);

  useEffect(() => {
    setSelectedOrders([]);
  }, [orders]);

  const capitalize = (str: string) =>
    str.charAt(0).toUpperCase() + str.slice(1);

  async function handleBulkAction(config: BulkActionConfig) {
    const i18nKey = capitalize(config.i18nKey);
    const hasConfirmed = window.confirm(
      intl.formatMessage(
        { id: `operationOrders.bulk${i18nKey}Confirmation` },
        { count: selectedCount }
      )
    );
    if (!hasConfirmed) {
      return;
    }
    setBulkState('loading');
    try {
      const orderIds = hasSelectedAllOrders
        ? []
        : selectedOrders?.map((order) => order.id);
      await onBulkAction?.(config.action, orderIds);
      toast.success(
        intl.formatMessage(
          { id: `operationOrders.bulk${i18nKey}Success` },
          { count: selectedCount }
        )
      );
    } catch (error) {
      console.warn({ error });
      toast.error(
        intl.formatMessage({ id: `operationOrders.bulk${i18nKey}Error` })
      );
    } finally {
      setBulkState('idle');
    }
  }

  return (
    <>
      <div className="card p-10 h-auto">
        <div className="d-flex align-items-center justify-content-between mb-8">
          <h2 className="card-title">
            {intl.formatMessage({ id: 'b2bOrders.title' })}
          </h2>
          <Dropdown>
            <Dropdown.Toggle variant="primary" id="dropdown-create-b2b-order">
              <span className="fa fa-plus me-3" />
              {intl.formatMessage({ id: 'b2bOrders.createNewOrder' })}
            </Dropdown.Toggle>
            <Dropdown.Menu>
              <Dropdown.Item className="p-4" onClick={onCreateOrder}>
                <span className="fa fa fa-list me-3" />
                {intl.formatMessage({ id: 'b2bOrders.createOrderFromScratch' })}
              </Dropdown.Item>
              <Dropdown.Item className="p-4" onClick={onParseEdi}>
                <span className="fa fa-paste me-3" />
                {intl.formatMessage({ id: 'b2bOrders.createOrderFromEDI' })}
              </Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>
        </div>
        <div className="row mb-8">{children}</div>
        {loading && (
          <div className="spinner-border text-primary" role="status" />
        )}

        {!loading && displayBulkActions && (
          <section className="d-flex px-0 gap-4 align-items-end mb-8">
            <div className="me-4">
              <label className={clsx('mb-2', !selectedCount && 'text-muted')}>
                {selectedCount > 0 ? (
                  <>
                    {intl.formatMessage(
                      { id: 'operationOrders.countSelectedOrders' },
                      { count: selectedCount }
                    )}
                  </>
                ) : (
                  <>
                    {intl.formatMessage({
                      id: 'operationOrders.noSelectedOrders',
                    })}
                  </>
                )}
              </label>
              <Dropdown>
                <Dropdown.Toggle variant="primary" disabled={selectedCount < 1}>
                  <span className="me-4 fas w-15px d-inline-block fa-layer-group" />
                  {intl.formatMessage({ id: 'operationOrders.bulkActions' })}
                </Dropdown.Toggle>
                <Dropdown.Menu>
                  {bulkActions.map((config) => (
                    <Dropdown.Item
                      key={config.action}
                      className="py-3 ps-5 fs-6"
                      onClick={() => handleBulkAction(config)}
                    >
                      <span
                        className={`me-4 fas w-15px d-inline-block ${config.icon} text-muted`}
                      />
                      {intl.formatMessage({
                        id: `operationOrders.${config.i18nKey}`,
                      })}
                    </Dropdown.Item>
                  ))}
                </Dropdown.Menu>
              </Dropdown>
            </div>
          </section>
        )}
        {!loading && orders.length === 0 && (
          <p className="fw-bolder">
            {intl.formatMessage({ id: 'b2bOrders.noOrders' })}
          </p>
        )}
        {!loading && orders.length > 0 && (
          <div
            className="table-responsive"
            style={{
              height: 'calc(100vh - 420px)',
            }}
          >
            <p className="text-muted mb-4">
              {intl.formatMessage(
                { id: 'b2bOrders.countOrders' },
                { visible: orders.length, count }
              )}
            </p>
            <table className="table table-hover table-row-dashed table-row-gray-200 align-start gs-0 gy-4">
              <thead className="table-header sticky-top bg-white">
                <tr className="fw-bolder">
                  {displayBulkActions && (
                    <th className="text-center ps-6 pe-4">
                      <input
                        type="checkbox"
                        className="cursor-pointer form-check w-25px h-25px"
                        disabled={bulkState === 'loading'}
                        checked={selectedOrders.length === orders.length}
                        onChange={() => {
                          if (selectedOrders.length === orders.length) {
                            setSelectedOrders([]);
                          } else {
                            setSelectedOrders(orders);
                          }
                        }}
                      />
                    </th>
                  )}
                  <th>{intl.formatMessage({ id: 'b2bOrders.order' })}</th>
                  <th>{intl.formatMessage({ id: 'b2bOrders.distributor' })}</th>
                  <th>{intl.formatMessage({ id: 'b2bOrders.origin' })}</th>
                  <th>
                    {intl.formatMessage({ id: 'b2bOrders.preparationDate' })}
                  </th>
                  <th>{intl.formatMessage({ id: 'b2bOrders.promiseDate' })}</th>
                  <th>{intl.formatMessage({ id: 'b2bOrders.lines' })}</th>
                  <th>{intl.formatMessage({ id: 'b2bOrders.comments' })}</th>
                  <th>{intl.formatMessage({ id: 'b2bOrders.shippingFee' })}</th>
                  <th>
                    {intl.formatMessage({ id: 'b2bOrders.totalDiscounts' })}
                  </th>
                  <th>
                    {intl.formatMessage({ id: 'b2bOrders.totalWithTaxes' })}
                  </th>
                  <th>
                    {intl.formatMessage({ id: 'b2bOrders.totalWithoutTaxes' })}
                  </th>
                  <th>{intl.formatMessage({ id: 'b2bOrders.totalWeight' })}</th>
                  <th>€/kg</th>
                  <th>{intl.formatMessage({ id: 'b2bOrders.city' })}</th>
                  <th>{intl.formatMessage({ id: 'b2bOrders.fullAddress' })}</th>
                  <th>
                    {intl.formatMessage({ id: 'b2bOrders.trackingLink' })}
                  </th>
                  <th>
                    {
                      // Empty th for the actions
                    }
                  </th>
                </tr>
              </thead>
              <tbody>
                {orders
                  ?.sort((a, b) => b.id - a.id)
                  ?.map((order) => (
                    <tr key={order.id}>
                      {displayBulkActions && (
                        <td className="text-center ps-6 pe-4">
                          <input
                            type="checkbox"
                            className="cursor-pointer form-check w-25px h-25px"
                            checked={selectedOrders.includes(order)}
                            disabled={bulkState === 'loading'}
                            onChange={() => {
                              if (selectedOrders.includes(order)) {
                                setSelectedOrders(
                                  selectedOrders.filter(
                                    (t) => t.id !== order.id
                                  )
                                );
                              } else {
                                setSelectedOrders([...selectedOrders, order]);
                              }
                            }}
                          />
                        </td>
                      )}
                      <td className="text-nowrap">
                        <div className="mb-1">{order.code}</div>
                        <div className="mb-1 text-muted">
                          {order.external_reference || '-'}{' '}
                        </div>
                        {STATE_TRANSITIONS[order.state]?.length > 0 ? (
                          <Dropdown>
                            <Dropdown.Toggle
                              id="dropdown-create-b2b-order"
                              variant={clsx({
                                primary:
                                  !SUCCESS_STATES.includes(order.state) &&
                                  !FAIL_STATES.includes(order.state),
                                success: SUCCESS_STATES.includes(order.state),
                                danger: FAIL_STATES.includes(order.state),
                              })}
                              className={clsx({
                                'badge badge-sm px-4 py-2 mb-1': true,
                              })}
                            >
                              {order.state}
                            </Dropdown.Toggle>
                            <Dropdown.Menu>
                              {STATE_TRANSITIONS[order.state]?.map((state) => (
                                <Dropdown.Item
                                  className="p-4"
                                  key={state}
                                  onClick={() => onUpdateState(order, state)}
                                >
                                  {state}
                                </Dropdown.Item>
                              ))}
                            </Dropdown.Menu>
                          </Dropdown>
                        ) : (
                          <div
                            style={{
                              width: 'fit-content',
                            }}
                            className={clsx({
                              'badge badge-sm px-4 py-2 d-block mb-1': true,
                              'badge-success': SUCCESS_STATES.includes(
                                order.state
                              ),
                              'badge-danger': FAIL_STATES.includes(order.state),
                            })}
                          >
                            {order.state}
                          </div>
                        )}
                        <div className="badge badge-dark px-4 py-2">
                          {order.is_sample ? (
                            <>
                              <span className="fas fa-gift me-2" />
                              {intl.formatMessage({
                                id: 'b2bOrders.sampleOrder',
                              })}
                            </>
                          ) : (
                            <>
                              <span className="fas fa-money-bill-wave me-2" />
                              {intl.formatMessage({
                                id: 'b2bOrders.regularOrder',
                              })}
                            </>
                          )}
                        </div>
                      </td>
                      <td>
                        {(() => {
                          const distributor = distributors.find(
                            (d) => d.id === order.distributor
                          );
                          return (
                            <a href={`/distributor/${order.distributor}`}>
                              {distributor?.name || '-'}
                              {distributor?.state === 'inactive' && (
                                <span className="text-muted">
                                  {' '}
                                  (
                                  {intl.formatMessage({
                                    id: 'b2bOrders.inactiveDistributor',
                                  })}
                                  )
                                </span>
                              )}
                            </a>
                          );
                        })()}
                      </td>
                      <td className="text-nowrap">
                        {order.origin ? (
                          intl.formatMessage({
                            id: ORIGIN_LABEL_IDS[order.origin],
                          })
                        ) : (
                          <span className="text-muted">
                            {intl.formatMessage({
                              id: 'b2bOrders.originUnspecified',
                            })}
                          </span>
                        )}
                      </td>
                      <td className="text-info fw-bold">
                        {order.preparation_date
                          ? intl.formatDate(new Date(order.preparation_date), {
                              day: '2-digit',
                              month: '2-digit',
                              year: 'numeric',
                            })
                          : '-'}
                      </td>
                      <td className="">
                        {order.promise_date
                          ? intl.formatDate(new Date(order.promise_date), {
                              day: '2-digit',
                              month: '2-digit',
                              year: 'numeric',
                            })
                          : '-'}
                      </td>
                      <td
                        className="cursor-pointer"
                        onClick={() => onEditLines(order)}
                      >
                        <div className="d-flex align-items-center">
                          {order?.order_lines?.length ? (
                            <ul className="list-unstyled d-block my-0 w-200px">
                              {order.order_lines
                                .sort((a, b) => a?.name?.localeCompare(b?.name))
                                .map((line) => (
                                  <li key={line.id}>
                                    <div className="">
                                      <strong>{line.units}</strong> x{' '}
                                      {line?.name}
                                    </div>
                                  </li>
                                ))}
                            </ul>
                          ) : (
                            <div className="w-100 text-muted">
                              {intl.formatMessage({ id: 'b2bOrders.noLines' })}
                            </div>
                          )}
                        </div>
                      </td>
                      <td>
                        {order?.comments?.length ? (
                          <p>{order?.comments}</p>
                        ) : (
                          <p className="text-muted">-</p>
                        )}
                      </td>
                      <td>
                        {intl.formatNumber(order.shipping_fee, {
                          style: 'currency',
                          currency: 'EUR',
                        })}
                      </td>
                      <td>
                        {order.discount_mode === 'absolute'
                          ? intl.formatNumber(order.total_discounts, {
                              style: 'currency',
                              currency: 'EUR',
                            })
                          : `${order.total_discounts}%`}
                      </td>
                      <td>
                        {intl.formatNumber(order.total_with_taxes, {
                          style: 'currency',
                          currency: 'EUR',
                        })}
                      </td>
                      <td>
                        {intl.formatNumber(order.total_products_wo_taxes, {
                          style: 'currency',
                          currency: 'EUR',
                        })}
                      </td>
                      <td className="text-nowrap">
                        {order?.total_kg > -1
                          ? `${intl.formatNumber(order.total_kg, {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            })} kg`
                          : '-'}
                      </td>
                      <td>
                        <span className="text-nowrap">
                          {order.euro_kg
                            ? `${intl.formatNumber(order.euro_kg, {
                                minimumFractionDigits: 4,
                                maximumFractionDigits: 4,
                              })} €`
                            : '-'}
                        </span>
                      </td>
                      <td>{order.city || '-'}</td>
                      <td>{order.full_address}</td>
                      <td>
                        <div className="w-150px text-start">
                          {order?.tracking_link ? (
                            <div className="d-flex flex-column gap-2">
                              <input
                                onClick={(e) => {
                                  e.stopPropagation();
                                }}
                                type="text"
                                value={order?.tracking_link}
                                className="form-control form-control-solid fs-8 py-1 px-3 w-100 text-truncate"
                                readOnly
                              />
                              <div className="d-flex gap-2 justify-content-between">
                                <button
                                  className="btn btn-primary btn-sm flex-1 w-100 py-2 px-2 d-flex flex-nowrap align-items-center justify-content-center"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    copyToClipboard(order?.tracking_link);
                                  }}
                                >
                                  <span className="fa fa-copy me-2"></span>
                                  {intl.formatMessage({
                                    id: 'orderTrackingHistory.copy',
                                  })}
                                </button>
                                <a
                                  onClick={(e) => e.stopPropagation()}
                                  className="btn btn-primary btn-sm flex-1 w-100 py-2 px-2 d-flex flex-nowrap align-items-center justify-content-center"
                                  href={order?.tracking_link}
                                  target="_blank"
                                  rel="noreferrer"
                                >
                                  <span className="fa fa-external-link-alt me-2"></span>
                                  {intl.formatMessage({
                                    id: 'orderTrackingHistory.open',
                                  })}
                                </a>
                              </div>
                            </div>
                          ) : (
                            '-'
                          )}
                        </div>
                      </td>
                      <td className="text-center">
                        <Dropdown>
                          <Dropdown.Toggle className="btn btn-sm btn-secondary">
                            {intl.formatMessage({ id: 'b2bOrders.actions' })}
                          </Dropdown.Toggle>
                          <Dropdown.Menu>
                            <Dropdown.Item
                              className="p-4"
                              onClick={() => onEditOrder(order)}
                              disabled={NON_EDITABLE_STATES.includes(
                                order.state
                              )}
                            >
                              <span className="fa fa-edit me-3"></span>
                              {intl.formatMessage({ id: 'b2bOrders.edit' })}
                            </Dropdown.Item>
                            <Dropdown.Item
                              className="p-4"
                              onClick={() => onEditLines(order)}
                              disabled={NON_EDITABLE_STATES.includes(
                                order.state
                              )}
                            >
                              <span className="fa fa-list me-3"></span>
                              {intl.formatMessage({
                                id: 'b2bOrders.editLines',
                              })}
                            </Dropdown.Item>
                            <Dropdown.Item
                              className="p-4"
                              onClick={() => onEditComments(order)}
                            >
                              <span className="fa fa-comment me-3"></span>
                              {intl.formatMessage({ id: 'b2bOrders.comment' })}
                            </Dropdown.Item>
                            <Dropdown.Item
                              className="p-4"
                              onClick={() => onGenerateProforma(order)}
                            >
                              <span className="fa fa-file-invoice me-3"></span>
                              {intl.formatMessage({
                                id: 'b2bOrders.generateProforma',
                              })}
                            </Dropdown.Item>
                            <Dropdown.Item
                              className="p-4"
                              onClick={() => onGenerateDeliveryNote(order)}
                            >
                              <span className="fa fa-file-alt me-3"></span>
                              {intl.formatMessage({
                                id: 'b2bOrders.generateDeliveryNote',
                              })}
                            </Dropdown.Item>
                          </Dropdown.Menu>
                        </Dropdown>
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
            {count > 0 && (
              <>
                <Pagination
                  className="mx-auto mt-4 d-flex justify-content-center"
                  count={Math.ceil(count / ORDERS_PER_PAGE)}
                  page={page}
                  onChange={(_, page) => onPageChange(page)}
                />
              </>
            )}
          </div>
        )}
      </div>
    </>
  );
}
